<script setup lang="ts">
/**
 * A sub-navigation strip of router links in the kit's underline-tab look: one
 * link per page of an area („Einbindungen · Freigaben · Protokoll“), the one
 * owning the current route underlined.
 *
 * These are LINKS, not ARIA tabs — each opens its own page, so the strip is a
 * `<nav>` and the current page carries `aria-current="page"`. Real `<a href>`
 * elements, so Ctrl/Cmd-click and middle-click open a new tab. ←/→, Home and
 * End walk the links as a convenience; Tab still reaches every link.
 *
 * Which tab is active is derived from the route, see `activeRouteTab`: the
 * longest matching path wins, `exact` and `activeNames` refine it. A strip
 * wider than its container scrolls sideways with faded edges, and keeps the
 * active tab in view when the route changes.
 *
 * The `end` slot holds trailing controls (a button on the right of the strip).
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { activeRouteTab, type NxRouteTab } from './routeTabs';

const props = withDefaults(
  defineProps<{
    items: readonly NxRouteTab[];
    /** Accessible name of the navigation landmark. */
    ariaLabel?: string;
    /** `sm` for a strip inside a panel, `md` under a page header. */
    size?: 'sm' | 'md';
  }>(),
  { ariaLabel: 'Unterbereiche', size: 'md' }
);

defineSlots<{ end?: () => unknown }>();

const route = useRoute();
const router = useRouter();

const resolved = computed(() =>
  props.items.map((item) => {
    const target = router.resolve(item.to);
    return {
      item,
      key: item.key ?? target.fullPath,
      path: target.path,
      exact: item.exact,
      activeNames: item.activeNames,
    };
  })
);

const activeIndex = computed(() =>
  activeRouteTab(resolved.value, { path: route.path, name: route.name })
);

function hasBadge(item: NxRouteTab): boolean {
  return item.badge !== null && item.badge !== undefined && item.badge !== '';
}

// --- overflow fades ---------------------------------------------------------

const scroller = ref<HTMLElement | null>(null);
const fadeStart = ref(false);
const fadeEnd = ref(false);

function measure() {
  const el = scroller.value;
  if (!el) return;
  // One pixel of slack: sub-pixel widths leave scrollLeft a hair short of the end.
  const max = el.scrollWidth - el.clientWidth;
  fadeStart.value = el.scrollLeft > 1;
  fadeEnd.value = el.scrollLeft < max - 1;
}

let observer: ResizeObserver | null = null;
onMounted(() => {
  measure();
  if (typeof ResizeObserver !== 'undefined' && scroller.value) {
    observer = new ResizeObserver(measure);
    observer.observe(scroller.value);
  }
  revealActive();
});
onBeforeUnmount(() => observer?.disconnect());

/** Scrolls the active link into the strip without moving the page. */
function revealActive() {
  const el = scroller.value;
  const link = el?.querySelector<HTMLElement>('[aria-current="page"]');
  if (!el || !link) return;
  const start = link.offsetLeft;
  const end = start + link.offsetWidth;
  if (start < el.scrollLeft) el.scrollLeft = start;
  else if (end > el.scrollLeft + el.clientWidth) el.scrollLeft = end - el.clientWidth;
  measure();
}

watch(activeIndex, () => void nextTick(revealActive));

// --- keyboard ---------------------------------------------------------------

function onKeydown(event: KeyboardEvent) {
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
  const links = [
    ...(scroller.value?.querySelectorAll<HTMLElement>('.nx-route-tabs__link:not(.is-disabled)') ??
      []),
  ];
  const at = links.indexOf(document.activeElement as HTMLElement);
  if (at < 0) return;
  const target =
    event.key === 'ArrowRight'
      ? (at + 1) % links.length
      : event.key === 'ArrowLeft'
        ? (at - 1 + links.length) % links.length
        : event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? links.length - 1
            : -1;
  if (target < 0) return;
  event.preventDefault();
  links[target]!.focus();
}
</script>

<template>
  <nav
    class="nx-route-tabs"
    :class="`size-${props.size}`"
    :aria-label="props.ariaLabel"
    data-testid="nx-route-tabs"
  >
    <div
      ref="scroller"
      class="nx-route-tabs__scroller"
      :class="{ 'fade-start': fadeStart, 'fade-end': fadeEnd }"
      @scroll.passive="measure"
      @keydown="onKeydown"
    >
      <ul class="nx-route-tabs__list">
        <li v-for="(entry, index) in resolved" :key="entry.key" class="nx-route-tabs__item">
          <span
            v-if="entry.item.disabled"
            class="nx-route-tabs__link is-disabled"
            aria-disabled="true"
          >
            <component
              :is="entry.item.icon"
              v-if="entry.item.icon"
              class="nx-route-tabs__icon"
              aria-hidden="true"
            />
            <span class="nx-route-tabs__label">{{ entry.item.label }}</span>
          </span>
          <RouterLink v-else v-slot="{ href, navigate }" :to="entry.item.to" custom>
            <a
              :href="href"
              class="nx-route-tabs__link"
              :class="{ 'is-active': index === activeIndex }"
              :aria-current="index === activeIndex ? 'page' : undefined"
              data-testid="nx-route-tabs-link"
              @click="navigate"
            >
              <component
                :is="entry.item.icon"
                v-if="entry.item.icon"
                class="nx-route-tabs__icon"
                :filled="index === activeIndex"
                aria-hidden="true"
              />
              <span class="nx-route-tabs__label">{{ entry.item.label }}</span>
              <span
                v-if="hasBadge(entry.item)"
                class="nx-route-tabs__badge"
                :aria-hidden="entry.item.badgeLabel ? 'true' : undefined"
                :title="entry.item.badgeLabel"
              >
                {{ entry.item.badge }}
              </span>
              <span v-if="hasBadge(entry.item) && entry.item.badgeLabel" class="nx-route-tabs__sr">
                {{ `, ${entry.item.badgeLabel}` }}
              </span>
            </a>
          </RouterLink>
        </li>
      </ul>
    </div>
    <div v-if="$slots.end" class="nx-route-tabs__end">
      <slot name="end" />
    </div>
  </nav>
</template>

<style scoped>
.nx-route-tabs {
  --route-tabs-fade: calc(var(--nxt-size-unit) * 1.5);

  display: flex;
  align-items: flex-end;
  gap: var(--nxt-space-sm);
  min-width: 0;
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-route-tabs__scroller {
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
}
.nx-route-tabs__scroller::-webkit-scrollbar {
  display: none;
}
.nx-route-tabs__scroller.fade-start {
  mask-image: linear-gradient(to right, transparent, black var(--route-tabs-fade));
}
.nx-route-tabs__scroller.fade-end {
  mask-image: linear-gradient(to left, transparent, black var(--route-tabs-fade));
}
.nx-route-tabs__scroller.fade-start.fade-end {
  mask-image: linear-gradient(
    to right,
    transparent,
    black var(--route-tabs-fade),
    black calc(100% - var(--route-tabs-fade)),
    transparent
  );
}

.nx-route-tabs__list {
  display: flex;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: inherit;
}

.nx-route-tabs__item {
  display: flex;
  flex: none;
}

.nx-route-tabs__link {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border-radius: var(--nxt-radius-md) var(--nxt-radius-md) 0 0;
  color: var(--nxt-tab-fg);
  font-size: var(--nxt-tab-size);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.size-sm .nx-route-tabs__link {
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
}

/* The indicator: a bar sitting ON the strip's baseline, so the active tab
   reads as the page the rule below belongs to. */
.nx-route-tabs__link::after {
  content: '';
  position: absolute;
  inset-inline: var(--nxt-space-xs);
  inset-block-end: 0;
  block-size: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-full) var(--nxt-radius-full) 0 0;
  background: transparent;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-route-tabs__link:hover:not(.is-disabled) {
  background: var(--nxt-tab-bg-hover);
  color: var(--nxt-tab-fg-hover);
}
.nx-route-tabs__link:hover:not(.is-disabled, .is-active)::after {
  background: var(--nxt-border-strong);
}
.nx-route-tabs__link.is-active {
  color: var(--nxt-tab-fg-selected);
}
.nx-route-tabs__link.is-active::after {
  background: var(--nxt-tab-indicator);
}
.nx-route-tabs__link.is-disabled {
  color: var(--nxt-tab-fg-disabled);
  cursor: not-allowed;
}
.nx-route-tabs__link:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

.nx-route-tabs__icon {
  flex: none;
  width: var(--nxt-icon-size-md);
  height: var(--nxt-icon-size-md);
}

.nx-route-tabs__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: calc(var(--nxt-size-unit) * 1.125);
  padding: 0 var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
  line-height: calc(var(--nxt-size-unit) * 1.125);
}
.nx-route-tabs__link.is-active .nx-route-tabs__badge {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}

.nx-route-tabs__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.nx-route-tabs__end {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding-block-end: var(--nxt-space-2xs);
}

@media (prefers-reduced-motion: reduce) {
  .nx-route-tabs__link,
  .nx-route-tabs__link::after {
    transition: none;
  }
}
</style>
